import { DeviceCategoryEnum } from "../../enums";
import { getStandardRoomSpaceValidation } from "./StandardRoomCompare/standardRoomCompareUtils";
import { IDevice, IMcBimItem, IPartialSpace, IRoomSummary } from "@backend/src/schemas";
import { ICeilingGrid, ITile } from "../../snapToGrid/snapToGrid";
import {
  getFireDeviceSquareBoxesAllCombinationsWithValidations,
  IFireDeviceSquareBoxesWithCoversValidation,
} from "./fireDeviceValidations.utils";
import { PartialMcBimSpace } from "../../utils";

export enum SPACE_VALIDATION_ID_ENUM {
  SUPPLY_AIR_FLOW = "SUPPLY_AIR_FLOW",
  RETURN_AIR_FLOW = "RETURN_AIR_FLOW",
  DEVICE_COLLISION = "DEVICE_COLLISION",
  STANDARD_ROOM = "STANDARD_ROOM",
  SENSOR_ONE_METER_FROM_AIR_DEVICE = "SENSOR_ONE_METER_FROM_AIR_DEVICE",
  SENSOR_HALF_METER_FROM_WALL = "SENSOR_HALF_METER_FROM_WALL",
  FIRE_DEVICES_MIN_200MM_FROM_WALL = "FIRE_DEVICES_MIN_200MM_FROM_WALL",
  ALL_FIRE_DEVICES_COVERS_WALL_WITH_4x3SQUARE = "ALL_FIRE_DEVICES_COVERS_WALL_WITH_4x3SQUARE",
  FIRE_DEVICES_MIN_2M_BETWEEN_FIRE_DEVICES = "FIRE_DEVICES_MIN_2M_BETWEEN_FIRE_DEVICES",
  DISTANCE_BETWEEN_SUPPLY_AND_RETURN_AIR_DEVIES_MIN_1200MM = "DISTANCE_BETWEEN_SUPPLY_AND_RETURN_AIR_DEVIES_MIN_1200MM",
  EVERY_DEVICE_IS_INSIDE_SPACE = "EVERY_DEVICE_IS_INSIDE_SPACE",
  LIGHT_DEVICES_MIN_500MM_FROM_WALL = "LIGHT_DEVICES_MIN_500MM_FROM_WALL",
}

export interface ISpaceValidation {
  id: SPACE_VALIDATION_ID_ENUM;
  status: "VALID" | "INVALID" | "WARNING" | "UNKNOWN" | undefined;
  title: string;
  errorText?: string;
  description: string;
  typeOfValidation: "SPATIAL" | "OTHER";
}

export const SPACE_VALIDATIONS: ISpaceValidation[] = [
  {
    id: SPACE_VALIDATION_ID_ENUM.SUPPLY_AIR_FLOW,
    title: "Tilluftsmengde",
    description: "Mengde sammenlignes med verdi som finnes på Revit Space",
    status: undefined,
    typeOfValidation: "OTHER",
  },
  {
    id: SPACE_VALIDATION_ID_ENUM.RETURN_AIR_FLOW,
    title: "Avtrekksmengde",
    description: "Mengde sammenlignes med verdi som finnes på Revit Space",
    status: undefined,
    typeOfValidation: "OTHER",
  },
  {
    id: SPACE_VALIDATION_ID_ENUM.DEVICE_COLLISION,
    title: "Objekt har ikke samme senterkoordinat",
    description: "Objekt har ikke samme senterkoordinat",
    status: undefined,
    typeOfValidation: "SPATIAL",
  },
  {
    id: SPACE_VALIDATION_ID_ENUM.STANDARD_ROOM,
    title: "Overensstemmende med standard rom",
    description: "Overensstemmende med standard rom",
    status: undefined,
    typeOfValidation: "OTHER",
  },
  {
    id: SPACE_VALIDATION_ID_ENUM.SENSOR_ONE_METER_FROM_AIR_DEVICE,
    title: "Sensor 1m fra tilluft og avtrekk",
    description: "Sensor er plassert >1m fra tilluft og avtrekk",
    status: undefined,
    typeOfValidation: "SPATIAL",
  },
  {
    id: SPACE_VALIDATION_ID_ENUM.SENSOR_HALF_METER_FROM_WALL,
    title: "Sensor 0.5m fra vegg",
    description: "Sensor 0.5m fra vegg",
    status: undefined,
    typeOfValidation: "SPATIAL",
  },
  {
    id: SPACE_VALIDATION_ID_ENUM.FIRE_DEVICES_MIN_200MM_FROM_WALL,
    title: "Sprinkler 200mm fra vegg",
    description: "Sprinkler 200mm fra vegg",
    status: undefined,
    typeOfValidation: "SPATIAL",
  },
  {
    id: SPACE_VALIDATION_ID_ENUM.ALL_FIRE_DEVICES_COVERS_WALL_WITH_4x3SQUARE,
    title: "Sprinkler dekker alle vegger",
    description:
      "Hvert sprinklerhode dekker et område på 3x4m. Denne regelen kontrollerer at alle vegger er dekket av denne overflaten. Alle mulige rotasjoner testet.",
    status: undefined,
    typeOfValidation: "SPATIAL",
  },
  {
    id: SPACE_VALIDATION_ID_ENUM.FIRE_DEVICES_MIN_2M_BETWEEN_FIRE_DEVICES,
    title: "Sprinkler min 2m mellom sprinkler",
    description: "",
    status: undefined,
    typeOfValidation: "SPATIAL",
  },
  {
    id: SPACE_VALIDATION_ID_ENUM.DISTANCE_BETWEEN_SUPPLY_AND_RETURN_AIR_DEVIES_MIN_1200MM,
    title: "Avstand mellom tilluft og avtrekk er minst 1.2m",
    description: "",
    status: undefined,
    typeOfValidation: "SPATIAL",
  },
  {
    id: SPACE_VALIDATION_ID_ENUM.EVERY_DEVICE_IS_INSIDE_SPACE,
    title: "Alle produktene er inne i rommet",
    description: "",
    status: undefined,
    typeOfValidation: "SPATIAL",
  },
  {
    id: SPACE_VALIDATION_ID_ENUM.LIGHT_DEVICES_MIN_500MM_FROM_WALL,
    title: "Lysarmatur minst 500mm fra vegg",
    description: "",
    status: undefined,
    typeOfValidation: "SPATIAL",
  },
];

interface ISpaceValArgs {
  singleRoom: IRoomSummary | null;
  singleSpace: PartialMcBimSpace;
  devicesInRoom: IDevice[];
  mcbimItemsInStandardSpace: IMcBimItem[];
  activeSpaceValidationSpecs: Record<SPACE_VALIDATION_ID_ENUM, boolean>;
  compCodePath: string;
}

export const getValidationsForSpace = (args: ISpaceValArgs): ISpaceValidation[] => {
  if (args.singleRoom == null) {
    return [];
  }
  return SPACE_VALIDATIONS.filter((validation, index) => {
    return args.activeSpaceValidationSpecs[validation.id];
  }).map((validation) => {
    switch (validation.id) {
      case SPACE_VALIDATION_ID_ENUM.SUPPLY_AIR_FLOW:
        if (args.singleRoom != null) {
          return getAirFlowValidation(args.singleRoom, args.devicesInRoom, "SUPPLY");
        }
        break;
      case SPACE_VALIDATION_ID_ENUM.RETURN_AIR_FLOW:
        if (args.singleRoom != null) {
          return getAirFlowValidation(args.singleRoom, args.devicesInRoom, "RETURN");
        }
        break;
      case SPACE_VALIDATION_ID_ENUM.DEVICE_COLLISION:
        return getSpaceValidationForDeviceInSameCoordinate(args.devicesInRoom);
      case SPACE_VALIDATION_ID_ENUM.STANDARD_ROOM:
        return getStandardRoomSpaceValidation({
          devices: args.devicesInRoom,
          mcbimItemsInStandardSpace: args.mcbimItemsInStandardSpace,
          compCodePath: args.compCodePath,
        });
      case SPACE_VALIDATION_ID_ENUM.SENSOR_ONE_METER_FROM_AIR_DEVICE:
        return getSensorsOneMeterFromSupployAirDeviceSpaceValidation(
          args.devicesInRoom,
          args.singleSpace
        );
      case SPACE_VALIDATION_ID_ENUM.SENSOR_HALF_METER_FROM_WALL:
        return {
          ...validation,
          errorText: "",
          status: sensorsAreHalfAMeterAwayFromWall(args.devicesInRoom, args.singleSpace)
            ? "VALID"
            : "WARNING",
        };
      case SPACE_VALIDATION_ID_ENUM.FIRE_DEVICES_MIN_200MM_FROM_WALL:
        return {
          ...validation,
          errorText: "",
          status: fireDevicesIsAtLeast200mmFromWall(args.devicesInRoom, args.singleSpace)
            ? "VALID"
            : "WARNING",
        };
      case SPACE_VALIDATION_ID_ENUM.ALL_FIRE_DEVICES_COVERS_WALL_WITH_4x3SQUARE:
        return {
          ...validation,
          errorText: "",
          status: getCombinationOfFireDeviceSquares({
            mcbimSpace: args.singleSpace,
            devicesInRoom: args.devicesInRoom,
          }).some((comb) => comb.coverAllWalls)
            ? "VALID"
            : "WARNING",
        };
      case SPACE_VALIDATION_ID_ENUM.FIRE_DEVICES_MIN_2M_BETWEEN_FIRE_DEVICES:
        return {
          ...validation,
          errorText: "",
          status: fireDevicesMin2mBetweenFireDevices(args.devicesInRoom) ? "VALID" : "WARNING",
        };
      case SPACE_VALIDATION_ID_ENUM.DISTANCE_BETWEEN_SUPPLY_AND_RETURN_AIR_DEVIES_MIN_1200MM:
        return {
          ...validation,
          errorText: "",
          status:
            getMinAndMaxDistanceBetweenTwoDeviceCategories(
              args.devicesInRoom,
              DeviceCategoryEnum.SUPPLY_AIR_DEVICE,
              DeviceCategoryEnum.RETURN_AIR_DEVICE
            ).min > 1.2
              ? "VALID"
              : "WARNING",
        };
      case SPACE_VALIDATION_ID_ENUM.EVERY_DEVICE_IS_INSIDE_SPACE:
        return getInsideSpaceValidation(args.devicesInRoom, args.singleSpace);
      case SPACE_VALIDATION_ID_ENUM.LIGHT_DEVICES_MIN_500MM_FROM_WALL:
        return {
          ...validation,
          errorText: "",
          status: lightDevicesMin500mmFromWall(args.devicesInRoom, args.singleSpace)
            ? "VALID"
            : "WARNING",
        };
      default:
        break;
    }
    return validation;
  });
};

const lightDevicesMin500mmFromWall = (
  devicesInRoom: IDevice[],
  space: PartialMcBimSpace
): boolean => {
  const lightDevices = devicesInRoom.filter(
    (device) =>
      device.deviceCategory === DeviceCategoryEnum.LIGHT_DEVICE ||
      device.deviceCategory === DeviceCategoryEnum.LIGHT_DEVICE_CIRCULAR
  );
  return lightDevices.every((device) => {
    const distance = getCoordinateDistanceFromPolygon(
      device.coordinate as number[],
      (space.polygon as number[][]) ?? []
    );
    return distance > 0.5;
  });
};

const getInsideSpaceValidation = (
  devicesInRoom: IDevice[],
  space: PartialMcBimSpace
): ISpaceValidation => {
  const validation = SPACE_VALIDATIONS.find(
    (validation) => validation.id === SPACE_VALIDATION_ID_ENUM.EVERY_DEVICE_IS_INSIDE_SPACE
  ) as ISpaceValidation;
  const nonGridDevices = devicesInRoom.filter((device) => device.grid == null);
  const devicesOutsideOfSpace = getDevicesOutsideOfSpace(nonGridDevices, space);
  let errorText = "";
  if (devicesOutsideOfSpace.length > 0) {
    errorText = `Følgende komponenter er utenfor rommet: ${devicesOutsideOfSpace
      .map((device) => device.compCode)
      .join(", ")}`;
  }
  return {
    ...validation,
    errorText,
    status: devicesOutsideOfSpace.length === 0 ? "VALID" : "WARNING",
  };
};

const getDevicesOutsideOfSpace = (
  devicesInRoom: IDevice[],
  space: PartialMcBimSpace
): IDevice[] => {
  return devicesInRoom.filter((device) => {
    if (device.coordinate == null) {
      return false;
    }
    return !coordinateIsInsidePolygon(
      device.coordinate as [number, number],
      space.polygon as [number, number][]
    );
  });
};

const coordinateIsInsidePolygon = (
  coordinate: [number, number],
  polygon: [number, number][]
): boolean => {
  try {
    const x = coordinate[0];
    const y = coordinate[1];
    let isInside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][0];
      const yi = polygon[i][1];
      const xj = polygon[j][0];
      const yj = polygon[j][1];
      const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
      if (intersect) {
        isInside = !isInside;
      }
    }
    return isInside;
  } catch (error) {
    console.error("Error in coordinateIsInsidePolygon:", error);
    return false;
  }
};

const getMinAndMaxDistanceBetweenTwoDeviceCategories = (
  devicesInRoom: IDevice[],
  deviceCategory1: DeviceCategoryEnum,
  deviceCategory2: DeviceCategoryEnum
): { min: number; max: number } => {
  const devices1 = devicesInRoom.filter((device) => device.deviceCategory === deviceCategory1);
  const devices2 = devicesInRoom.filter((device) => device.deviceCategory === deviceCategory2);
  let min = Infinity;
  let max = 0;
  devices1.forEach((device) => {
    devices2.forEach((_device) => {
      const distance = getDistanceBetweenToCoordinates(
        device.coordinate as number[],
        _device.coordinate as number[]
      );
      if (distance < min) {
        min = distance;
      }
      if (distance > max) {
        max = distance;
      }
    });
  });
  return { min, max };
};

const fireDevicesMin2mBetweenFireDevices = (devicesInRoom: IDevice[]): boolean => {
  return (
    getMinAndMaxDistanceBetweenSameDeviceCategory(
      devicesInRoom,
      DeviceCategoryEnum.FIRE_SUPPRESSION_DEVICE
    ).min > 2
  );
};

const getMinAndMaxDistanceBetweenSameDeviceCategory = (
  devicesInRoom: IDevice[],
  deviceCategory: DeviceCategoryEnum
): { min: number; max: number } => {
  const devices = devicesInRoom.filter((device) => device.deviceCategory === deviceCategory);
  let min = Infinity;
  let max = 0;
  devices.forEach((device) => {
    devices.forEach((_device) => {
      if (device._id !== _device._id) {
        const distance = getDistanceBetweenToCoordinates(
          device.coordinate as number[],
          _device.coordinate as number[]
        );
        if (distance < min) {
          min = distance;
        }
        if (distance > max) {
          max = distance;
        }
      }
    });
  });
  return { min, max };
};

const everyPartOfTheLineOfThePolygonHasAFireDeviceWithin2m = (
  devicesInRoom: IDevice[],
  space: IMcBimItem
): boolean => {
  const fireDevices = devicesInRoom.filter(
    (device) => device.deviceCategory === DeviceCategoryEnum.FIRE_SUPPRESSION_DEVICE
  );
  const polygon = space.polygon as number[][];
  for (let i = 0; i < polygon.length; i++) {
    const line = [polygon[i], polygon[(i + 1) % polygon.length]];
    const minDistance = getMinDistanceToAnyPartOfLineFromListOfDevices(
      line[0] as [number, number],
      line[1] as [number, number],
      fireDevices
    );

    if (minDistance !== Infinity) {
      if (minDistance > 1.5 && minDistance) {
        return false;
      }
    }
  }
  return true;
};

const getMinDistanceToAnyPartOfLineFromListOfDevices = (
  startPointOfLine: [number, number],
  endPointOfLine: [number, number],
  devices: IDevice[]
): number => {
  let minDistance = Infinity;

  const distanceToSegment = (
    point: [number, number],
    start: [number, number],
    end: [number, number]
  ): number => {
    const [px, py] = point;
    const [sx, sy] = start;
    const [ex, ey] = end;

    const dx = ex - sx;
    const dy = ey - sy;
    const lengthSquared = dx * dx + dy * dy;
    let t = ((px - sx) * dx + (py - sy) * dy) / lengthSquared;
    t = Math.max(0, Math.min(1, t));

    const closestPoint = [sx + t * dx, sy + t * dy];
    return Math.hypot(px - closestPoint[0], py - closestPoint[1]);
  };

  devices.forEach((device) => {
    const distance = distanceToSegment(
      device.coordinate as [number, number],
      startPointOfLine,
      endPointOfLine
    );

    if (!isNaN(distance)) {
      if (distance < minDistance) {
        minDistance = distance;
      }
    }
  });

  return minDistance;
};

const fireDevicesIsAtLeast200mmFromWall = (
  devicesInRoom: IDevice[],
  space: PartialMcBimSpace
): boolean => {
  const fireDevices = devicesInRoom.filter(
    (device) => device.deviceCategory === DeviceCategoryEnum.FIRE_SUPPRESSION_DEVICE
  );
  return fireDevices.every((device) => {
    const distance = getCoordinateDistanceFromPolygon(
      device.coordinate as number[],
      (space.polygon as number[][]) ?? []
    );
    return distance > 0.2;
  });
};

const getSensorsOneMeterFromSupployAirDeviceSpaceValidation = (
  devicesInRoom: IDevice[],
  singleSpace: PartialMcBimSpace
): ISpaceValidation => {
  const validation = SPACE_VALIDATIONS.find(
    (validation) => validation.id === SPACE_VALIDATION_ID_ENUM.SENSOR_ONE_METER_FROM_AIR_DEVICE
  ) as ISpaceValidation;
  return {
    ...validation,
    errorText: "",
    status: sensorsAreOneMeterAwayFromAirDevices(devicesInRoom, singleSpace) ? "VALID" : "WARNING",
  };
};

const getAirFlowValidation = (
  room: IRoomSummary,
  devicesInRoom: IDevice[],
  supplyOrReturn: "SUPPLY" | "RETURN"
): ISpaceValidation => {
  const totalAirFlowInDevices = devicesInRoom.reduce((acc, device) => {
    if (supplyOrReturn === "SUPPLY") {
      if (
        device.deviceCategory === DeviceCategoryEnum.SUPPLY_AIR_DEVICE &&
        device.supplyAirFlow != null
      ) {
        acc += device.supplyAirFlow;
      }
    }
    if (supplyOrReturn === "RETURN") {
      if (
        device.deviceCategory === DeviceCategoryEnum.RETURN_AIR_DEVICE &&
        device.returnAirFlow != null
      ) {
        acc += device.returnAirFlow;
      }
    }
    return acc;
  }, 0);
  const totalSupplyAirFlowInRoom =
    supplyOrReturn === "SUPPLY" ? (room?.supplyAirFlow ?? 0) : (room?.returnAirFlow ?? 0);
  const spaceValidation = SPACE_VALIDATIONS.find(
    (validation) =>
      validation.id ===
      (supplyOrReturn === "SUPPLY"
        ? SPACE_VALIDATION_ID_ENUM.SUPPLY_AIR_FLOW
        : SPACE_VALIDATION_ID_ENUM.RETURN_AIR_FLOW)
  ) as ISpaceValidation;
  return {
    ...spaceValidation,
    status: totalAirFlowInDevices === totalSupplyAirFlowInRoom ? "VALID" : "WARNING",
    errorText: `Revit MEP Space: ${totalSupplyAirFlowInRoom}, Summerat rom: ${totalAirFlowInDevices}, Diff: ${totalAirFlowInDevices - totalSupplyAirFlowInRoom}`,
  };
};

export const getSpaceValidationForDeviceInSameCoordinate = (
  devicesInRoom: IDevice[]
): ISpaceValidation => {
  const numberOfItemsInSameTile = devicesInRoomHasSameCoordinate(devicesInRoom);
  const spaceValidation = SPACE_VALIDATIONS.find(
    (validation) => validation.id === SPACE_VALIDATION_ID_ENUM.DEVICE_COLLISION
  ) as ISpaceValidation;
  return {
    ...spaceValidation,
    status: numberOfItemsInSameTile > 0 ? "WARNING" : "VALID",
    errorText: `Antall objekter i samme platta: ${numberOfItemsInSameTile}`,
  };
};

const devicesInRoomHasSameCoordinate = (devicesInRoom: IDevice[]): number => {
  return devicesInRoom.reduce((acc, device) => {
    if (device.coordinate == null) {
      return acc;
    }
    const otherDevices = devicesInRoom.filter(
      (_device) => _device._id !== device._id && _device.coordinate != null
    );
    // Check if any other device has a coordinate less than 0.1 meters away
    const hasCloseCoordinate = otherDevices.some(
      (_device) =>
        getDistanceBetweenToCoordinates(
          device.coordinate as number[],
          _device.coordinate as number[]
        ) < 0.1
    );
    if (hasCloseCoordinate) {
      return acc + 1;
    }
    return acc;
  }, 0);
};

export const sensorsAreOneMeterAwayFromAirDevices = (
  devicesInRoom: IDevice[],
  space: PartialMcBimSpace
): boolean => {
  if (space == null) {
    return false;
  }
  const airDevices = devicesInRoom.filter(
    (device) =>
      device.deviceCategory === DeviceCategoryEnum.SUPPLY_AIR_DEVICE ||
      device.deviceCategory === DeviceCategoryEnum.RETURN_AIR_DEVICE
  );
  const sensors = devicesInRoom.filter(
    (device) => device.deviceCategory === DeviceCategoryEnum.SENSOR_DEVICE
  );

  const sensorsAreWithin1MeterFromSupplyAirDevice = sensors.some((sensor) => {
    return airDevices.some((supplyDevice) => {
      const distance = getDistanceBetweenToCoordinates(
        sensor.coordinate as number[],
        supplyDevice.coordinate as number[]
      );
      return distance < 1;
    });
  });

  return !sensorsAreWithin1MeterFromSupplyAirDevice;
};

export const sensorsAreHalfAMeterAwayFromWall = (
  devicesInRoom: IDevice[],
  space: PartialMcBimSpace
) => {
  const sensors = devicesInRoom.filter(
    (device) => device.deviceCategory === DeviceCategoryEnum.SENSOR_DEVICE
  );
  return sensors.every((sensor) => {
    const distance = getCoordinateDistanceFromPolygon(
      sensor.coordinate as number[],
      (space.polygon as number[][]) ?? []
    );
    return distance > 0.5;
  });
};

export const getDistanceBetweenToCoordinates = (
  coordinate1: number[],
  coordinate2: number[]
): number => {
  const [x1, y1] = coordinate1;
  const [x2, y2] = coordinate2;
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
};

const getCoordinateDistanceFromPolygon = (coordinate: number[], polygon: number[][]): number => {
  const [x1, y1] = coordinate;

  const distanceToSegment = (
    x: number,
    y: number,
    xA: number,
    yA: number,
    xB: number,
    yB: number
  ): number => {
    const A = x - xA;
    const B = y - yA;
    const C = xB - xA;
    const D = yB - yA;

    const dot = A * C + B * D;
    const lenSq = C * C + D * D;
    const param = lenSq !== 0 ? dot / lenSq : -1;

    let xx, yy;

    if (param < 0) {
      xx = xA;
      yy = yA;
    } else if (param > 1) {
      xx = xB;
      yy = yB;
    } else {
      xx = xA + param * C;
      yy = yA + param * D;
    }

    const dx = x - xx;
    const dy = y - yy;
    return Math.sqrt(dx * dx + dy * dy);
  };

  let minDistance = Infinity;

  for (let i = 0; i < polygon.length; i++) {
    const [xA, yA] = polygon[i];
    const [xB, yB] = polygon[(i + 1) % polygon.length];
    const distance = distanceToSegment(x1, y1, xA, yA, xB, yB);
    if (distance < minDistance) {
      minDistance = distance;
    }
  }

  return minDistance;
};

export const allValidationsAreValid = (spaceValidations: ISpaceValidation[]): boolean => {
  return spaceValidations.every((validation) => validation.status === "VALID");
};

interface IArg {
  devicesInRoom: IDevice[];
  deviceOnTheMoveId: string;
  ceilingGrid: ICeilingGrid;
  tile: ITile;
  room: IRoomSummary;
  space: IMcBimItem | IPartialSpace;
  mcbimItemsInStandardSpace: IMcBimItem[];
  activeSpaceValidationSpecs: Record<SPACE_VALIDATION_ID_ENUM, boolean>;
  compCodePath: string;
}

export const isTileInCeilingGridValid = (args: IArg): boolean => {
  const devicesInRoomWithDeviceOnTheMovePlacedInTile = args.devicesInRoom.map((device) => {
    if (device._id === args.deviceOnTheMoveId) {
      return {
        ...device,
        coordinate: [args.tile.center[0], args.tile.center[1]] as [number, number],
      };
    }
    return device;
  });

  const filteredActiveSpaceValidationSpecs = Object.fromEntries(
    Object.entries(args.activeSpaceValidationSpecs).filter(
      ([key, value]) =>
        SPACE_VALIDATIONS.find((validation) => validation.id === key)?.typeOfValidation ===
        "SPATIAL"
    )
  ) as Record<SPACE_VALIDATION_ID_ENUM, boolean>;

  const validationsInSpaceWithDevicOnTheMoveInTile = getValidationsForSpace({
    singleRoom: args.room,
    singleSpace: args.space,
    devicesInRoom: devicesInRoomWithDeviceOnTheMovePlacedInTile,
    mcbimItemsInStandardSpace: args.mcbimItemsInStandardSpace,
    activeSpaceValidationSpecs: filteredActiveSpaceValidationSpecs,
    compCodePath: args.compCodePath,
  });
  return validationsInSpaceWithDevicOnTheMoveInTile.every(
    (validation) => validation.status === "VALID"
  );
};

interface ICombArgs {
  mcbimSpace: PartialMcBimSpace;
  devicesInRoom: IDevice[];
}

export const getCombinationOfFireDeviceSquares = (
  args: ICombArgs
): IFireDeviceSquareBoxesWithCoversValidation[] => {
  const fireDeviceCoordinates = args.devicesInRoom.reduce(
    (acc, device) => {
      if (device.deviceCategory === "FIRE_SUPPRESSION_DEVICE") {
        if (device.coordinate != null) {
          acc.push([device.coordinate[0], device.coordinate[1]]);
        }
      }
      return acc;
    },
    [] as [number, number][]
  );

  return getFireDeviceSquareBoxesAllCombinationsWithValidations({
    spacePolygon: args.mcbimSpace?.polygon ?? [],
    fireDeviceCoordinates,
    squareSize: {
      width: 2.05,
      height: 1.55,
    },
  });
};
